import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { ISavingsProduct } from '../types';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface ISearchProductsResponse {
  status?: boolean;
  message?: string;
  data?: ISavingsProduct[];
  errors?: IApiResponseError[];
}

type ISavingsProductsMap = Record<string, ISavingsProduct>;

const initialState: IApiState<ISavingsProductsMap> = {
  value: null,
  status: 'idle',
  error: '',
};

export const searchSavingsProductAsync = createAsyncThunk(
  'savings/products/search',
  async (productId: string, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.post<ISearchProductsResponse>(
        '/savings/products/search',
        { searchParams: { id: productId } },
        getSavingsAuthConfig(state),
      );

      return { productId, product: data?.data?.[0] ?? null };
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;
      return rejectWithValue(error.response?.data);
    }
  },
);

export const searchSavingsProductsSlice = createSlice({
  name: 'savings/products/search',
  initialState,
  reducers: {
    resetSearchSavingsProducts(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchSavingsProductAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(searchSavingsProductAsync.fulfilled, (state, action) => {
        state.status = 'success';
        const { productId, product } = action.payload;
        if (product) {
          state.value = { ...(state.value ?? {}), [productId]: product };
        }
      })
      .addCase(searchSavingsProductAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetSearchSavingsProducts } =
  searchSavingsProductsSlice.actions;

export default searchSavingsProductsSlice.reducer;
